import { useEffect, useState } from 'react';
import useUserStore from '@/stores/UserStore';
import { Save } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';

import { setUserCopilotSettings } from '@/hooks/agent';

import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Separator } from '../ui/separator';
import { Textarea } from '../ui/textarea';

// Constants for AI Services
const AI_SERVICES = [
  { value: 'openai', label: 'OpenAI' },
  { value: 'gemini', label: 'Gemini' },
  { value: 'claude', label: 'Claude' },
];

const PROMPT_TYPES = [
  {
    value: 'default',
    label: 'Default System Prompt',
    description: 'Use the optimized default system prompt',
  },
  {
    value: 'custom',
    label: 'Custom System Prompt',
    description: 'Create and customize your own system prompt',
  },
];

const Copilot = () => {
  const [customPrompt, setCustomPrompt] = useState('');
  const [selectionType, setSelectionType] = useState('default');
  const [selectedAIService, setSelectedAIService] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const navigate = useNavigate();

  const {
    copilotEnabled,
    user_copilot_ai_selection,
    user_copilot_prompt,
    user_copilot_ai_service,
    setUserCopilotPrompt,
    setUserCopilotAISelection,
    setUserCopilotAIService,
  } = useUserStore(
    useShallow((state) => ({
      copilotEnabled: state.copilotEnabled,
      user_copilot_ai_selection: state.user_copilot_ai_selection,
      user_copilot_prompt: state.user_copilot_prompt,
      user_copilot_ai_service: state.user_copilot_ai_service,
      setUserCopilotPrompt: state.setUserCopilotPrompt,
      setUserCopilotAISelection: state.setUserCopilotAISelection,
      setUserCopilotAIService: state.setUserCopilotAIService,
    }))
  );

  // Initialize component state from store
  useEffect(() => {
    console.log('copilot page called');
    if (copilotEnabled === false) {
      navigate('/settings');
    }

    if (user_copilot_ai_selection === 'custom') {
      setCustomPrompt(user_copilot_prompt);
      setSelectionType(user_copilot_ai_selection);
      setSelectedAIService(user_copilot_ai_service);
    } else if (user_copilot_ai_selection === 'default') {
      setSelectionType('default');
      setCustomPrompt('');
      setSelectedAIService('');
    }
  }, [
    user_copilot_prompt,
    user_copilot_ai_service,
    user_copilot_ai_selection,
    copilotEnabled,
    navigate,
  ]);

  // Check if there are unsaved changes
  const hasChanges = () => {
    if (selectionType === 'default') {
      return user_copilot_ai_selection !== 'default';
    } else if (selectionType === 'custom') {
      return (
        user_copilot_prompt !== customPrompt.trim() ||
        user_copilot_ai_service !== selectedAIService
      );
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    let copilotData = {};

    if (selectionType === 'default') {
      // if default copilot is selected
      copilotData = {
        selection: selectionType,
      };
    } else {
      // if custom copilot is selected
      if (!customPrompt.trim()) {
        toast.error('Please enter a custom prompt or switch to default');
        return;
      }
      if (!selectedAIService) {
        toast.error('Please select an AI model');
        return;
      }

      copilotData = {
        selection: selectionType,
        prompt: customPrompt.trim(),
        service: selectedAIService,
      };
    }

    toast.promise(setUserCopilotSettings(copilotData), {
      loading: 'Saving copilot settings...',
      success: () => {
        if (copilotData.selection === 'default') {
          setUserCopilotPrompt(null);
          setUserCopilotAISelection('default');
          setIsSaving(false);
        } else {
          setUserCopilotPrompt(copilotData.prompt);
          setUserCopilotAISelection('custom');
          setUserCopilotAIService(copilotData.service);
          setIsSaving(false);
        }
        return 'Copilot settings saved successfully';
      },
      error: (error) => {
        return (
          error.response?.data?.message || 'Unable to save copilot settings'
        );
      },
    });
  };

  // Show loading state until user_copilot_ai_selection is not null
  if (user_copilot_ai_selection === null) {
    return (
      <div className='flex min-h-[200px] items-center justify-center'>
        <span className='text-muted-foreground'>
          Loading Copilot settings...
        </span>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <div className='flex justify-between'>
        <div>
          <h3 className='text-lg font-medium'>Copilot</h3>
          <p className='text-sm text-muted-foreground'>
            Configure your system prompt for the AI copilot.
          </p>
        </div>
      </div>
      <Separator />

      {user_copilot_ai_selection !== null && (
        <div className='space-y-6'>
          {/* Prompt Type Selection */}
          <div className='space-y-4'>
            <div className='space-y-2'>
              <Label className='text-base'>Prompt Type</Label>
            </div>

            <RadioGroup
              value={selectionType}
              onValueChange={setSelectionType}
              className='grid gap-3'
            >
              {PROMPT_TYPES.map((type) => (
                <div key={type.value}>
                  <RadioGroupItem
                    value={type.value}
                    id={type.value}
                    className='peer sr-only'
                  />
                  <Label
                    htmlFor={type.value}
                    className='flex cursor-pointer items-center justify-between rounded-lg border-2 border-muted bg-background p-4 hover:bg-accent peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5 [&:has([data-state=checked])]:border-primary [&:has([data-state=checked])]:bg-primary/5'
                  >
                    <div className='space-y-1'>
                      <p className='text-sm font-medium leading-none peer-data-[state=checked]:text-primary'>
                        {type.label}
                      </p>
                      <p className='text-xs text-muted-foreground'>
                        {type.description}
                      </p>
                    </div>
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* AI Model Selection - Only show for custom prompt */}
          {selectionType === 'custom' && (
            <div className='space-y-2'>
              <Label htmlFor='ai-service' className='text-base'>
                AI Service
              </Label>
              <Select
                value={selectedAIService}
                onValueChange={setSelectedAIService}
              >
                <SelectTrigger className='w-full'>
                  <SelectValue placeholder='Select AI service' />
                </SelectTrigger>
                <SelectContent>
                  {AI_SERVICES.map((service) => (
                    <SelectItem key={service.value} value={service.value}>
                      {service.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className='text-xs text-muted-foreground'>
                Choose the AI service that will process your custom prompt.
              </p>
            </div>
          )}

          {/* Custom Prompt Section */}
          {selectionType === 'custom' && (
            <div className='space-y-4'>
              <div className='space-y-2'>
                <Label htmlFor='custom-prompt' className='text-base'>
                  Custom System Prompt
                </Label>
                <p className='text-sm text-muted-foreground'>
                  Define your own system prompt to customize how the AI copilot
                  behaves. This prompt will guide the AI&apos;s responses and
                  behavior.
                </p>
              </div>

              <Textarea
                id='custom-prompt'
                placeholder='Enter your custom system prompt here...'
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                className='min-h-[200px] resize-none'
              />
              <p className='text-xs text-muted-foreground'>
                The system prompt helps define the AI&apos;s role, tone, and
                behavior. Be specific about how you want the copilot to respond
                to users.
              </p>
            </div>
          )}

          {/* Status Indicator for Default Prompt */}
          {selectionType === 'default' && (
            <div className='rounded-lg bg-muted p-4'>
              <p className='text-sm text-muted-foreground'>
                Currently using the default system prompt.
              </p>
            </div>
          )}

          {/* Action Buttons - Only show when there are changes */}
          {hasChanges() && (
            <div className='flex gap-3'>
              <Button
                onClick={handleSave}
                disabled={
                  isSaving ||
                  (selectionType === 'custom' &&
                    (!customPrompt.trim() || !selectedAIService))
                }
                className='flex items-center gap-2'
              >
                <Save className='h-4 w-4' />
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Copilot;
